import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["display", "form", "input"]
  static values = { 
    url: String,
    field: String
  }

  // Constants for better maintainability
  static TEXTAREA_MIN_HEIGHT = 60
  static TEXTAREA_MAX_HEIGHT = 200
  static DEFAULT_DATE_FORMAT = 'dd/mm/yyyy'

  connect() {
    this.originalValue = this.getOriginalValue()
  }

  getOriginalValue() {
    if (this.fieldValue === 'notes') {
      // For notes, use the data attribute which contains the raw notes
      return this.displayTarget.dataset.originalNotes || ""
    } else if (this.fieldValue === 'description') {
      // For category description, use the data attribute which contains the raw description
      return this.displayTarget.dataset.originalDescription || ""
    } else if (this.fieldValue === 'start_time' || this.fieldValue === 'end_time') {
      // For datetime fields, get the value from the appropriate data attribute
      return this.fieldValue === 'start_time' 
        ? this.displayTarget.dataset.startTime || ""
        : this.displayTarget.dataset.endTime || ""
    } else if (this.fieldValue === 'category') {
      // For category, get the category ID from data attribute
      return this.displayTarget.dataset.categoryId || ""
    } else {
      // Fallback to text content
      return this.displayTarget.textContent.trim()
    }
  }

  edit() {
    // Hide display, show form
    this.displayTarget.style.display = "none"
    this.formTarget.style.display = "block"
    
    // Focus and select text in input
    this.inputTarget.focus()
    if (this.fieldValue === 'notes' || this.fieldValue === 'description') {
      // Move cursor to end of text instead of selecting all
      const textLength = this.inputTarget.value.length
      this.inputTarget.setSelectionRange(textLength, textLength)
      // Auto-resize textarea to content
      this.autoResizeTextarea()
      // Add input listener for continuous resizing
      this.inputTarget.addEventListener('input', () => this.autoResizeTextarea())
    } else if (this.fieldValue === 'category') {
      // For category dropdown, just focus (no select)
      this.inputTarget.focus()
    } else {
      // For text inputs (like name), select the text
      this.inputTarget.select()
    }
  }

  autoResizeTextarea() {
    if ((this.fieldValue === 'notes' || this.fieldValue === 'description') && this.inputTarget.tagName.toLowerCase() === 'textarea') {
      // Reset height to auto to get the correct scrollHeight
      this.inputTarget.style.height = 'auto'
      
      // Calculate the new height based on content
      const minHeight = this.constructor.TEXTAREA_MIN_HEIGHT
      const maxHeight = this.constructor.TEXTAREA_MAX_HEIGHT
      const scrollHeight = this.inputTarget.scrollHeight
      
      // Set height to content or within min/max bounds
      const newHeight = Math.min(Math.max(scrollHeight, minHeight), maxHeight)
      this.inputTarget.style.height = newHeight + 'px'
      
      // Enable scrolling if content exceeds max height
      if (scrollHeight > maxHeight) {
        this.inputTarget.style.overflowY = 'auto'
      } else {
        this.inputTarget.style.overflowY = 'hidden'
      }
    }
  }

  cancel() {
    // Reset to original value
    this.inputTarget.value = this.originalValue
    
    // Remove input listener if it exists
    if (this.fieldValue === 'notes' || this.fieldValue === 'description') {
      this.inputTarget.removeEventListener('input', () => this.autoResizeTextarea())
      // Reset textarea height
      this.inputTarget.style.height = this.constructor.TEXTAREA_MIN_HEIGHT + 'px'
      this.inputTarget.style.overflowY = 'hidden'
    }
    
    // Hide form, show display
    this.formTarget.style.display = "none"
    this.displayTarget.style.display = "block"
  }

  async save(event) {
    event.preventDefault()
    
    // Client-side validation for time fields
    if (this.fieldValue === 'start_time' || this.fieldValue === 'end_time') {
      const validationError = this.validateTimeFields()
      if (validationError) {
        this.showFeedback(validationError, "text-red-600")
        return
      }
    }
    
    // Create and submit a regular form
    const form = document.createElement('form')
    form.method = 'POST'
    form.action = this.urlValue
    
    // Add CSRF token
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    if (csrfToken) {
      const csrfInput = document.createElement('input')
      csrfInput.type = 'hidden'
      csrfInput.name = 'authenticity_token'
      csrfInput.value = csrfToken
      form.appendChild(csrfInput)
    }
    
    // Add method override
    const methodInput = document.createElement('input')
    methodInput.type = 'hidden'
    methodInput.name = '_method'
    methodInput.value = 'PATCH'
    form.appendChild(methodInput)
    
    // Add inline edit flag
    const inlineInput = document.createElement('input')
    inlineInput.type = 'hidden'
    inlineInput.name = 'inline_edit'
    inlineInput.value = 'true'
    form.appendChild(inlineInput)
    
    // Add the field that was edited
    const fieldInput = document.createElement('input')
    fieldInput.type = 'hidden'
    fieldInput.name = 'edited_field'
    fieldInput.value = this.fieldValue
    form.appendChild(fieldInput)
    
    // Add the field data
    const fieldDataInput = document.createElement('input')
    fieldDataInput.type = 'hidden'
    fieldDataInput.name = this.getFieldParam()
    fieldDataInput.value = this.inputTarget.value
    form.appendChild(fieldDataInput)
    
    // For time fields, include both start and end times
    if (this.fieldValue === 'start_time') {
      const endTimeInput = document.createElement('input')
      endTimeInput.type = 'hidden'
      endTimeInput.name = 'time_log[end_time]'
      endTimeInput.value = this.displayTarget.dataset.endTime
      form.appendChild(endTimeInput)
    } else if (this.fieldValue === 'end_time') {
      const startTimeInput = document.createElement('input')
      startTimeInput.type = 'hidden'
      startTimeInput.name = 'time_log[start_time]'
      startTimeInput.value = this.displayTarget.dataset.startTime
      form.appendChild(startTimeInput)
    }
    
    document.body.appendChild(form)
    form.submit()
  }

  getFieldParam() {
    if (this.fieldValue === 'notes') {
      return "time_log[notes]"
    } else if (this.fieldValue === 'name') {
      return "category[name]"
    } else if (this.fieldValue === 'description') {
      return "category[description]"
    } else if (this.fieldValue === 'category') {
      return "time_log[category_id]"
    } else if (this.fieldValue === 'start_time') {
      return "time_log[start_time]"
    } else if (this.fieldValue === 'end_time') {
      return "time_log[end_time]"
    }
  }

  validateTimeFields() {
    let startTime, endTime
    
    // Get the appropriate times based on which field is being edited
    if (this.fieldValue === 'start_time') {
      startTime = this.inputTarget.value
      endTime = this.displayTarget.dataset.endTime
    } else if (this.fieldValue === 'end_time') {
      endTime = this.inputTarget.value
      startTime = this.displayTarget.dataset.startTime
    }
    
    // Validate that both times are present
    if (!startTime || !endTime) {
      return "Both start and end times are required"
    }
    
    // Parse and validate date formats
    const startDateTime = new Date(startTime)
    const endDateTime = new Date(endTime)
    
    if (isNaN(startDateTime.getTime()) || isNaN(endDateTime.getTime())) {
      return "Invalid date/time format"
    }
    
    // Validate that end time is after start time
    if (endDateTime <= startDateTime) {
      return "End time must be after start time"
    }
    
    return null // No validation errors
  }

  updateDisplay() {
    if (this.fieldValue === 'notes') {
      const newValue = this.inputTarget.value || "No notes"
      
      // Update the data attribute with the new raw value
      this.displayTarget.dataset.originalNotes = this.inputTarget.value
      
      if (newValue === "No notes" || newValue === "") {
        this.displayTarget.innerHTML = '<span class="text-gray-400 italic">No notes</span>'
      } else {
        // Convert newlines to <br> tags for display, similar to simple_format
        const formattedValue = newValue.replace(/\n/g, '<br>')
        this.displayTarget.innerHTML = `<div class="text-gray-600 dark:text-gray-300">${formattedValue}</div>`
      }
    } else if (this.fieldValue === 'name') {
      // For category name, just update the text content
      this.displayTarget.textContent = this.inputTarget.value
    } else if (this.fieldValue === 'description') {
      const newValue = this.inputTarget.value || ""
      
      // Update the data attribute with the new raw value
      this.displayTarget.dataset.originalDescription = newValue
      
      if (newValue === "" || !newValue.trim()) {
        this.displayTarget.innerHTML = '<span class="text-gray-400 italic">No description</span>'
      } else {
        // Convert newlines to <br> tags for display, similar to simple_format
        const formattedValue = newValue.replace(/\n/g, '<br>')
        this.displayTarget.innerHTML = `
          <div class="text-gray-600 dark:text-gray-300">
            <div class="block md:hidden">${this.truncateText(formattedValue, 40)}</div>
            <div class="hidden md:block lg:hidden">${this.truncateText(formattedValue, 70)}</div>
            <div class="hidden lg:block">${this.truncateText(formattedValue, 120)}</div>
          </div>`
      }
    } else if (this.fieldValue === 'category') {
      // For category, update display with selected option text
      const selectedOption = this.inputTarget.options[this.inputTarget.selectedIndex]
      if (selectedOption) {
        // Check if we're using the custom category select
        const categorySelectController = this.element.querySelector('[data-controller="category-select"]')
        if (categorySelectController) {
          // Update the custom dropdown display with color
          const displayElement = this.element.querySelector('[data-category-select-target="display"]')
          if (displayElement) {
            const color = selectedOption.dataset.color || '#6B7280'
            const name = selectedOption.dataset.name || selectedOption.text
            displayElement.innerHTML = `
              <div class="w-3 h-3 rounded-full flex-shrink-0" style="background-color: ${color}"></div>
              <span>${name}</span>
            `
          }
          // Update the main display element
          this.displayTarget.innerHTML = `
            <div class="flex items-center gap-2">
              <div class="w-3 h-3 rounded-full flex-shrink-0" style="background-color: ${selectedOption.dataset.color || '#6B7280'}"></div>
              <span>${selectedOption.text}</span>
            </div>
          `
        } else {
          // Fallback for regular select
          this.displayTarget.textContent = selectedOption.text
        }
        this.displayTarget.dataset.categoryId = selectedOption.value
      }
    } else if (this.fieldValue === 'start_time' || this.fieldValue === 'end_time') {
      // For datetime fields, format using the same format as the helper
      const newValue = this.inputTarget.value
      if (newValue) {
        const date = new Date(newValue)
        
        // Get user's date format from the meta tag
        const dateFormat = document.querySelector('meta[name="user-date-format"]')?.content || this.constructor.DEFAULT_DATE_FORMAT
        
        // Format date according to user's preference
        const month = String(date.getMonth() + 1).padStart(2, '0')
        const day = String(date.getDate()).padStart(2, '0')
        const year = date.getFullYear()
        
        let datePart
        switch (dateFormat) {
          case 'dd/mm/yyyy':
            datePart = `${day}/${month}/${year}`
            break
          case 'yyyy-mm-dd':
            datePart = `${year}-${month}-${day}`
            break
          case 'mm/dd/yyyy':
          default:
            datePart = `${month}/${day}/${year}`
        }
        
        // Format time exactly like the helper (hh:mm AM/PM)
        const hours = date.getHours()
        const minutes = String(date.getMinutes()).padStart(2, '0')
        const ampm = hours >= 12 ? 'PM' : 'AM'
        const hours12 = hours % 12 || 12
        const timePart = `${String(hours12).padStart(2, '0')}:${minutes} ${ampm}`
        
        // Update display with formatted date and time
        const formattedDateTime = `${datePart} ${timePart}`
        this.displayTarget.textContent = formattedDateTime
        
        // Update data attributes for future edits
        if (this.fieldValue === 'start_time') {
          this.displayTarget.dataset.startTime = newValue
          // Also update the end time display target in the same row
          this.updateRowTimeDataAttribute('end_time', this.displayTarget.dataset.endTime)
        } else {
          this.displayTarget.dataset.endTime = newValue
          // Also update the start time display target in the same row
          this.updateRowTimeDataAttribute('start_time', this.displayTarget.dataset.startTime)
        }
      }
    }
  }

  updateRowTimeDataAttribute(fieldType, timeValue) {
    // Find the row that contains this inline edit controller
    const row = this.element.closest('tr')
    if (row && timeValue) {
      // Find the other time field in the same row and update its data attributes
      const otherTimeCell = fieldType === 'start_time' ? row.children[1] : row.children[2]
      if (otherTimeCell) {
        const otherDisplayElement = otherTimeCell.querySelector('[data-inline-edit-target="display"]')
        if (otherDisplayElement) {
          if (fieldType === 'start_time') {
            otherDisplayElement.dataset.startTime = timeValue
          } else {
            otherDisplayElement.dataset.endTime = timeValue
          }
        }
      }
    }
  }

  updateDurationFields() {
    // Get the current start and end times
    let startTime, endTime
    
    if (this.fieldValue === 'start_time') {
      startTime = this.inputTarget.value
      endTime = this.displayTarget.dataset.endTime
    } else if (this.fieldValue === 'end_time') {
      endTime = this.inputTarget.value
      startTime = this.displayTarget.dataset.startTime
    }
    
    if (!startTime || !endTime) return
    
    // Calculate duration
    const duration = this.calculateDuration(startTime, endTime)
    
    if (duration) {
      // Find the row that contains this inline edit controller
      const row = this.element.closest('tr')
      if (row) {
        // Find and update the duration cell (4th column) with formatted display
        const durationCell = row.children[3]
        if (durationCell) {
          const durationDisplay = this.formatDurationDisplay(duration.minutes)
          const durationDiv = durationCell.querySelector('div')
          if (durationDiv) {
            durationDiv.textContent = durationDisplay
          }
          // Update the data-order attribute for sorting
          durationCell.setAttribute('data-order', duration.minutes.toString())
        }
      }
    }
  }

  calculateDuration(startTimeStr, endTimeStr) {
    try {
      const startTime = new Date(startTimeStr)
      const endTime = new Date(endTimeStr)
      
      // Validate parsed dates
      if (isNaN(startTime.getTime()) || isNaN(endTime.getTime())) {
        return null
      }
      
      if (endTime <= startTime) {
        return null
      }
      
      // Calculate difference in milliseconds
      const diffMs = endTime - startTime
      
      // Convert to minutes
      const minutes = Math.round(diffMs / (1000 * 60))
      
      // Convert to hours (rounded to 2 decimal places)
      const hours = (minutes / 60).toFixed(2)
      
      return {
        minutes: minutes,
        hours: parseFloat(hours)
      }
    } catch (error) {
      return null
    }
  }

  formatDurationDisplay(totalMinutes) {
    const hours = Math.floor(totalMinutes / 60)
    const remainingMinutes = totalMinutes % 60

    if (hours > 0) {
      return `${hours}h ${remainingMinutes}m`
    } else {
      return `${totalMinutes}m`
    }
  }

  truncateText(text, length) {
    if (text.length <= length) {
      return text
    }
    return text.substring(0, length) + '...'
  }

  showFeedback(message, className) {
    const feedback = document.createElement("div")
    feedback.textContent = message
    feedback.className = `${className} text-xs mt-1 font-normal`
    
    // Find the table cell container (the parent of displayTarget)
    const cellContainer = this.displayTarget.parentElement
    
    // Position the feedback below the display content
    cellContainer.appendChild(feedback)
    
    setTimeout(() => {
      feedback.remove()
    }, 2000)
  }

  handleKeydown(event) {
    if (event.key === "Escape") {
      this.cancel()
    } else if (event.key === "Enter" && (event.ctrlKey || event.metaKey || this.fieldValue === 'category')) {
      // For category dropdown, allow Enter without Ctrl/Cmd modifier
      this.save(event)
    }
  }
} 